import { Component } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { LanguageService } from '../../../../../core/services/language.service';

export interface IssueCategoryItem {
  id: string;
  categoryId: number;
  titleTH: string;
  titleEN: string;
  descTH: string;
  descEN: string;
  icon: string;
  badgeTH?: string;
  badgeEN?: string;
  colorBg: string;
  iconColor: string;
}

export interface FaqItem {
  questionTH: string;
  questionEN: string;
  answerTH: string;
  answerEN: string;
  isOpen?: boolean;
}

@Component({
  selector: 'app-report-main',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './report-main.component.html',
  styleUrl: './report-main.component.css'
})
export class ReportMainComponent {
  currentLang: 'TH' | 'EN' = 'TH';
  phone: string = '02-0962255 ต่อ 8';
  rawPhone: string = '020962255';
  searchText: string = '';

  // สำหรับควบคุม Modal ยืนยันการโทรออก
  showCallModal: boolean = false;
  selectedCategoryForCall: IssueCategoryItem | null = null;

  // รายการหมวดหมู่ปัญหาหลัก ใช้ FontAwesome icon (fa-solid) ป้องกันปัญหาบน iPhone / iOS Safari
  categories: IssueCategoryItem[] = [
    {
      id: 'hotline',
      categoryId: 1,
      titleTH: 'ติดต่อเจ้าหน้าที่สายด่วน',
      titleEN: 'Contact Support Hotline',
      descTH: 'โทรติดต่อสายด่วน Call Center เพื่อแจ้งปัญหาทั่วไปหรือปัญหาค่าบริการ ตลอด 24 ชั่วโมง',
      descEN: 'Call our hotline to report general or billing issues 24/7',
      icon: 'fa-solid fa-headset',
      badgeTH: 'สายด่วน',
      badgeEN: 'Hotline',
      colorBg: 'bg-amber-50 text-amber-700 border-amber-100',
      iconColor: 'text-amber-500'
    },
    {
      id: 'my_cases',
      categoryId: 6,
      titleTH: 'ติดตามปัญหา',
      titleEN: 'Track cases',
      descTH: 'ตรวจสอบสถานะและประวัติการแจ้งเรื่องร้องเรียน/ช่วยเหลือกับเจ้าหน้าที่',
      descEN: 'Track status and history of your reported service cases',
      icon: 'fa-solid fa-list-check',
      badgeTH: 'ติดตามปัญหา',
      badgeEN: 'Tracking',
      colorBg: 'bg-emerald-50 text-emerald-700 border-emerald-100',
      iconColor: 'text-emerald-500'
    },
    // {
    //   id: 'app_report',
    //   categoryId: 5,
    //   titleTH: 'แจ้งปัญหาแอปพลิเคชัน',
    //   titleEN: 'Report App Issues',
    //   descTH: 'กรอกแบบฟอร์มแจ้งปัญหาการเข้าใช้งานระบบ หน้าจอขัดข้อง หรือข้อมูลบัญชีไม่ถูกต้อง',
    //   descEN: 'Submit a form for login failures, UI bugs, or account detail issues',
    //   icon: 'fa-solid fa-mobile-screen-button',
    //   badgeTH: 'แจ้งฟอร์ม',
    //   badgeEN: 'Form',
    //   colorBg: 'bg-indigo-50 text-indigo-600 border-indigo-100',
    //   iconColor: 'text-indigo-500'
    // }
  ];

  // คำถามที่พบบ่อย (FAQ)
  faqs: FaqItem[] = [
    {
      questionTH: 'ทำไมปัญหาลานจอดรถหรือค่าบริการ ต้องโทรติดต่อเจ้าหน้าที่?',
      questionEN: 'Why do parking or billing issues require calling support?',
      answerTH: 'เนื่องจากปัญหาลานจอดรถและค่าบริการต้องอาศัยการประสานงานตรวจสอบหน้างานแบบทันที (Real-time) เจ้าหน้าที่ฝ่ายบริการลูกค้าจะเป็นผู้รับเรื่องและเปิดเคสช่วยเหลือเพื่อความรวดเร็วสูงสุด',
      answerEN: 'Because parking and billing issues require real-time verification and immediate on-site coordination, calling support ensures the fastest resolution.',
      isOpen: false
    },
    {
      questionTH: 'แจ้งปัญหาแอปพลิเคชันใช้เวลานานเท่าใดในการแก้ไข?',
      questionEN: 'How long does it take to resolve reported app issues?',
      answerTH: 'หลังส่งแบบฟอร์มแล้ว เจ้าหน้าที่ฝ่ายพัฒนาระบบจะรับเรื่องและตรวจสอบเพื่อดำเนินการแก้ไขข้อผิดพลาดภายใน 24-48 ชั่วโมง',
      answerEN: 'After submitting the form, our system development team will review and resolve the issue within 24-48 hours.',
      isOpen: false
    }
  ];

  constructor(languageService: LanguageService, private router: Router) {
    languageService.currentLang$.subscribe((lang) => {
      this.currentLang = lang;
    });
  }

  /*#################################################### Function ################################################*/

  // Getter สำหรับกรองหมวดหมู่ตามข้อความค้นหา
  get filteredCategories(): IssueCategoryItem[] {
    if (!this.searchText || this.searchText.trim() === '') {
      return this.categories;
    }
    const query = this.searchText.toLowerCase().trim();
    return this.categories.filter((cat) => {
      const matchTH = cat.titleTH.toLowerCase().includes(query) || cat.descTH.toLowerCase().includes(query);
      const matchEN = cat.titleEN.toLowerCase().includes(query) || cat.descEN.toLowerCase().includes(query);
      return matchTH || matchEN;
    });
  }

  // เมื่อคลิกหมวดหมู่ปัญหา
  onCategoryClick(cat: IssueCategoryItem) {
    if (cat.id === 'app_report') {
      this.router.navigate(['/report-other']);
    } else if (cat.id === 'my_cases') {
      this.router.navigate(['/customer-service-case']);
    } else {
      this.selectedCategoryForCall = cat;
      this.showCallModal = true;
    }
  }

  // ยืนยันการโทรออก
  confirmCall() {
    this.showCallModal = false;
    window.location.href = `tel:${this.rawPhone}`;
  }

  // ปิด Modal ยืนยัน
  closeCallModal() {
    this.showCallModal = false;
    this.selectedCategoryForCall = null;
  }

  // โทรออกสายด่วน Call Center
  callSupport() {
    window.location.href = `tel:${this.rawPhone}`;
  }

  // เปิด/ปิด การ์ด FAQ
  toggleFaq(index: number) {
    this.faqs[index].isOpen = !this.faqs[index].isOpen;
  }

  // ย้อนกลับไปยังหน้าหลัก
  goback() {
    setTimeout(() => {
      this.router.navigate(['/home']);
    }, 0);
  }

  /*############################################# API #################################################*/
}
